import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventClassResponse, EventClassRegistrationResponse, ClassRoundResponse, ClassResultResponse, CompanyResponse, HorseResponse} from '../../../../../../../../../../@shared/models/response.module';
import {formatDateDMYDots} from '../../../../../../../../../../@core/utils/utils.service';
import {DeleteModal} from '../../../../../../../../../../@shared/components/delete-modal/delete-modal';
import {HorseDetailsModal} from '../../../../../../../../../../@shared/components/horse-details-modal/horse-details-modal';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {ScoringService} from '../../../../../../../../../../@shared/services/scoring.service';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {EditRegistration} from './components/edit-registration/edit-registration';
import {AddClassRegistration} from './components/add-class-registration/add-class-registration';
import {ResultEntryModal} from '../result-entry-modal/result-entry-modal';
import {DocumentViewerModal} from '../../../../../../../../../../@shared/components/document-viewer-modal/document-viewer-modal';
import {AQH_EVE_32, AQH_EVE_6, AQH_EVE_150} from '../../../../../../../../../../@shared/models/input.module';
import {EndpointService} from '../../../../../../../../../../@shared/endpoints.service';
import {StorageService} from '../../../../../../../../../../@core/services/storage.service';
import {firstValueFrom} from 'rxjs';

@Component({
  selector: 'app-view-class-registrations',
  standalone: false,
  templateUrl: './view-class-registrations.html',
  styleUrl: './view-class-registrations.css'
})
export class ViewClassRegistrations implements OnInit {
  @Input() eventClass!: EventClassResponse;
  @Input() companyId!: string;
  @Input() company?: CompanyResponse;
  loading = false;
  rounds: ClassRoundResponse[] = [];
  selectedRound: ClassRoundResponse | null = null;
  results: ClassResultResponse[] = [];
  resultsMap: Map<number, ClassResultResponse> = new Map(); // registration_id -> result

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private eventsService: EventsService,
    private scoringService: ScoringService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private storageService: StorageService,
    private endpointService: EndpointService
  ) {}

  ngOnInit() {
    console.log('Event Class with Registrations:', this.eventClass);
    // If registrations are not loaded, reload the event class first
    if (!this.eventClass.event_class_registrations || this.eventClass.event_class_registrations.length === 0) {
      this.reloadEventClass();
    }
    this.loadRounds();
  }

  get customPaymentMethodName(): string | null {
    return this.company?.custom_payment_method_name || null;
  }

  get hasCustomPayment(): boolean {
    return this.company?.custom_payment_enabled === true;
  }

  async loadRounds() {
    try {
      this.rounds = await firstValueFrom(
        this.scoringService.getRounds({ event_class_id: this.eventClass.event_class_id })
      ) || [];
      // Auto-select first round if available
      if (this.rounds.length > 0) {
        this.selectedRound = this.rounds[0];
        await this.loadResults();
      }
    } catch (e) {
      this.rounds = [];
    }
  }

  async loadResults() {
    if (!this.selectedRound) {
      this.results = [];
      this.resultsMap.clear();
      return;
    }

    try {
      this.results = await firstValueFrom(
        this.scoringService.getResults({
          event_class_id: this.eventClass.event_class_id,
          round_id: this.selectedRound.round_id
        })
      ) || [];

      // Build map for quick lookup
      this.resultsMap.clear();
      for (const result of this.results) {
        this.resultsMap.set(result.class_registration_id, result);
      }
    } catch (e) {
      this.results = [];
      this.resultsMap.clear();
    }
  }

  getResultForRegistration(registration: EventClassRegistrationResponse): ClassResultResponse | undefined {
    return this.resultsMap.get(registration.class_registration_id);
  }

  reloadEventClass() {
    if (!this.eventClass) return;

    this.loading = true;
    const payload: AQH_EVE_6 = {
      event_id: this.eventClass.event_id
    };

    this.eventsService.getEventClasses(payload).subscribe({
      next: (classes: EventClassResponse[]) => {
        // Find and update the current event class
        const updatedClass = classes.find(c => c.event_class_id === this.eventClass.event_class_id);
        if (updatedClass) {
          this.eventClass = updatedClass;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error(this.translateService.instant('manageEvent.viewRegistrations.reloadError'));
      }
    });
  }

  close() {
    this.activeModal.close();
  }

  getRegistrationNumber(index: number): number {
    return index + 1;
  }

  openAddRegistrationModal() {
    const modalRef = this.modalService.open(AddClassRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.eventClass = this.eventClass;

    modalRef.result.then((added) => {
      if (added) {
        // Reload the event class data to show new registration
        this.reloadEventClass();
      }
    }, () => {
      // Modal dismissed
    });
  }

  openEditRegistrationModal(registration: EventClassRegistrationResponse) {
    const modalRef = this.modalService.open(EditRegistration, { size: 'lg', centered: true });
    modalRef.componentInstance.registration = registration;

    modalRef.result.then((updated) => {
      if (updated) {
        // Reload the event class data to show updated registration
        this.reloadEventClass();
        // Also signal parent to refresh
        this.activeModal.close(true);
      }
    }, () => {
      // Modal dismissed
    });
  }

  deleteRegistration(registration: EventClassRegistrationResponse) {
    const modalRef = this.modalService.open(DeleteModal, { size: 'md', centered: true });
    modalRef.result.then((confirmed) => {
      if (confirmed) {
        const payload: AQH_EVE_32 = {
          class_registration_id: registration.class_registration_id,
          user_id: registration.user_id
        };

        this.eventsService.cancelEventClassRegistration(payload).subscribe({
          next: () => {
            this.toastService.success(this.translateService.instant('manageEvent.viewRegistrations.cancelSuccess'));
            // Remove the registration from the list
            const index = this.eventClass.event_class_registrations.findIndex(
              r => r.class_registration_id === registration.class_registration_id
            );
            if (index > -1) {
              this.eventClass.event_class_registrations.splice(index, 1);
            }
          },
          error: () => {
            this.toastService.error(this.translateService.instant('manageEvent.viewRegistrations.cancelError'));
          }
        });
      }
    }, () => {
      // Modal dismissed
    });
  }

  openResultEntryModal(registration: EventClassRegistrationResponse) {
    if (!this.selectedRound) {
      this.toastService.error(this.translateService.instant('results.noRoundSelected'));
      return;
    }

    const existingResult = this.getResultForRegistration(registration);

    const modalRef = this.modalService.open(ResultEntryModal, { size: 'lg', centered: true });
    modalRef.componentInstance.eventClass = this.eventClass;
    modalRef.componentInstance.registration = registration;
    modalRef.componentInstance.round = this.selectedRound;
    modalRef.componentInstance.existingResult = existingResult;

    modalRef.result.then((saved) => {
      if (saved) {
        this.loadResults(); // Reload results to show updated data
      }
    }, () => {});
  }

  viewResultImages(registration: EventClassRegistrationResponse) {
    const result = this.getResultForRegistration(registration);
    if (!result?.images?.length) {
      return;
    }

    const riderName = registration.user
      ? `${registration.user.first_name || ''} ${registration.user.last_name || ''}`.trim()
      : 'Participant';

    const modalRef = this.modalService.open(DocumentViewerModal, { size: 'lg', centered: true });
    modalRef.componentInstance.images = result.images;
    modalRef.componentInstance.title = `${riderName} - ${this.translateService.instant('images.resultImages')}`;
  }

  async selectRound(round: ClassRoundResponse) {
    this.selectedRound = round;
    await this.loadResults();
  }

  markAsPaid(registration: EventClassRegistrationResponse, paymentMethod: 'cash' | 'bank_transfer' | 'other') {
    const payload: AQH_EVE_150 = {
      company_id: this.companyId,
      class_registration_id: registration.class_registration_id,
      payment_status: 'paid',
      payment_method: paymentMethod
    };

    this.endpointService.aqh_eve_150(payload).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('manageEvent.viewRegistrations.markPaidSuccess'));
        // Update the registration in the list
        registration.payment_status = 'paid';
        registration.payment_method = paymentMethod;
      },
      error: () => {
        this.toastService.error(this.translateService.instant('manageEvent.viewRegistrations.markPaidError'));
      }
    });
  }

  // Horse helper methods
  getGenderLabel(gender: number | null | undefined): string {
    if (gender === null || gender === undefined) return '-';
    const genders: { [key: number]: string } = {
      0: 'Mare',
      1: 'Stallion',
      2: 'Gelding'
    };
    return genders[gender] || '-';
  }

  getHorseAge(horse: HorseResponse): string {
    if (!horse?.date_of_birth) return '-';
    const birthDate = new Date(horse.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} ${this.translateService.instant('horseDetails.years')}`;
  }

  isVaccinationOverdue(horse: HorseResponse): boolean {
    if (!horse?.next_vaccination_date) return false;
    const nextVaccDate = new Date(horse.next_vaccination_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return nextVaccDate < today;
  }

  openHorseDetailsModal(horse: HorseResponse) {
    const modalRef = this.modalService.open(HorseDetailsModal, { size: 'lg', centered: true });
    modalRef.componentInstance.horse = horse;
  }

  /**
   * Calculate net amount after platform + payment fees
   * Stripe: 2.4% + 2.00 NOK, Platform: 2%
   * Net = price × 0.956 - 2.00 per transaction
   */
  calculateNetAmount(grossAmount: number, transactionCount: number): number {
    if (!grossAmount || grossAmount <= 0) return 0;
    // Apply fee per transaction: (gross × 0.956) - (2.00 × transactions)
    const net = grossAmount * 0.956 - (2 * transactionCount);
    return Math.max(0, Math.round(net * 100) / 100);
  }

  getTotalRevenue(): number {
    const registrations = this.eventClass?.event_class_registrations?.length || 0;
    const price = this.eventClass?.price || 0;
    return registrations * price;
  }

  getNetRevenue(): number {
    const registrations = this.eventClass?.event_class_registrations?.length || 0;
    const grossRevenue = this.getTotalRevenue();
    return this.calculateNetAmount(grossRevenue, registrations);
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
